import {getHighlightPostText} from "../utils/eventUtils";
import {IEvent, IGame, IGameShowResponse, ILocalRef} from "@webng-types/write-model";
import {TeaserContentType} from "../types";
import {useMemo} from "react";

export function eventsForRefs(refList: {refs?: ILocalRef[]|IEvent[]}|undefined, events: IEvent[], reverse: boolean) {
  if(refList && refList.refs) {
    const ret = refList.refs.map(ref => events.find(event => event.local_id === ref.local_id)).filter(e => !!e) as IEvent[]
    if(reverse) ret.reverse()
    return ret
  } else {
    return []
  }
}

function isPinned(event: IEvent) {
  return event.highlight === 'sticky' || event.highlight === 'stickyNoHighlight'
}

function dedupeByLocalId(events: IEvent[]) {
  return events.filter((event, index) => events.findIndex(e => e.local_id === event.local_id) === index)
}

// Sorted most-recent-first, matching the chronological order convention already used by the
// "newest"/"all" branches below. Deliberately uses each post's own created_at rather than
// Event.sort (whose flag bits float pinned/highlighted posts to the top elsewhere in the
// product) - "highlightsPinned" must not float pinned posts to the top of this list.
function byCreatedAtDesc(a: IEvent, b: IEvent) {
  return (b.created_at ?? 0) - (a.created_at ?? 0)
}

export function selectTeaserWidgetEvents({game, events, content, limit}: {game: IGame, events: IEvent[], content: TeaserContentType, limit: number}): IEvent[] {
  const chronological = eventsForRefs({refs: game.events}, events, false)

  if(content === 'highlights') {
    return eventsForRefs(game.summary?.highlights, events, false).filter(h => getHighlightPostText(h) !== "").slice(0, limit)
  } else if(content === 'highlightsPinned') {
    const highlighted = eventsForRefs(game.summary?.highlights, events, false)
    // The sticky ref list is maintained incrementally by game-manager: a post pinned before that
    // list existed is missing from it until the post is edited or the game is rebuilt. The
    // chronological window is scanned as well so such posts still show up (design.md Decision 2).
    const pinned = [...eventsForRefs(game.summary?.sticky, events, false), ...chronological.filter(isPinned)]
    const merged = dedupeByLocalId([...highlighted, ...pinned]).sort(byCreatedAtDesc)
    return merged.filter(h => getHighlightPostText(h) !== "").slice(0, limit)
  } else if(content === 'newest') {
    return chronological.filter(h => !isPinned(h)).filter(h => getHighlightPostText(h) !== "").slice(0, limit)
  } else {
    // 'all': pinned + highlights + latest, unfiltered, in game.events order (pins first) - this is
    // the teaser's pre-existing "newest" behavior, carried forward under its own value (see proposal.md).
    return chronological.filter(h => getHighlightPostText(h) !== "").slice(0, limit)
  }
}

export function useTeaserWidgetEvents({gameShowResponse, content, limit}: {gameShowResponse: IGameShowResponse, content: TeaserContentType, limit: number}): {events: IEvent[], game: IGame} {
  const game = gameShowResponse.game!;
  const events = gameShowResponse.events;

  let eventsToShow = useMemo(() => {
    return selectTeaserWidgetEvents({game, events: events || [], content, limit})
  }, [events, game, content, limit])

  return {events: eventsToShow, game}
}
